import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  TextField,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Save,
  Refresh,
  Security,
  Notifications,
  Storage,
  Speed,
  ExpandMore
} from '@mui/icons-material';
import adminApi from '../services/api';

interface SystemSettings {
  rateLimiting: {
    enabled: boolean;
    requestsPerMinute: number;
    burstLimit: number;
  };
  security: {
    requireAttestation: boolean;
    allowSoftwareAttestation: boolean;
    blockFailedDevices: boolean;
    maxFailedAttempts: number;
  };
  logging: {
    level: string;
    enableAuditLog: boolean;
    retentionDays: number;
  };
  notifications: {
    emailAlerts: boolean;
    securityAlerts: boolean;
    systemAlerts: boolean;
    alertEmail: string;
  };
  performance: {
    cacheEnabled: boolean;
    cacheTtlMinutes: number;
    connectionPoolSize: number;
  };
}

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSettings>({
    rateLimiting: {
      enabled: true,
      requestsPerMinute: 60,
      burstLimit: 10
    },
    security: {
      requireAttestation: true,
      allowSoftwareAttestation: false,
      blockFailedDevices: true,
      maxFailedAttempts: 5
    },
    logging: {
      level: 'INFO',
      enableAuditLog: true,
      retentionDays: 90
    },
    notifications: {
      emailAlerts: false,
      securityAlerts: true,
      systemAlerts: true,
      alertEmail: ''
    },
    performance: {
      cacheEnabled: true,
      cacheTtlMinutes: 15,
      connectionPoolSize: 10
    }
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getSettings();
      if (response.settings) {
        setSettings(response.settings);
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load settings';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (section: keyof SystemSettings, key: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
    setPendingChanges(true);
    setSuccess(null);
    setError(null);
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await adminApi.updateSettings(settings);
      
      setPendingChanges(false);
      setSuccess('Settings saved successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to save settings';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetSettings = () => {
    setOpenDialog(true);
  };

  const confirmReset = () => {
    setSettings({
      rateLimiting: {
        enabled: true,
        requestsPerMinute: 60,
        burstLimit: 10
      },
      security: {
        requireAttestation: true,
        allowSoftwareAttestation: false,
        blockFailedDevices: true,
        maxFailedAttempts: 5
      },
      logging: {
        level: 'INFO',
        enableAuditLog: true,
        retentionDays: 90
      },
      notifications: {
        emailAlerts: false,
        securityAlerts: true,
        systemAlerts: true,
        alertEmail: ''
      },
      performance: {
        cacheEnabled: true,
        cacheTtlMinutes: 15,
        connectionPoolSize: 10
      }
    });
    setPendingChanges(true);
    setOpenDialog(false);
    setSuccess('Settings reset to defaults');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold">
          ⚙️ System Settings
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadSettings}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={handleResetSettings}
          >
            Reset to Defaults
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSaveSettings}
            disabled={!pendingChanges || loading}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {pendingChanges && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have unsaved changes. Click "Save Changes" to apply them.
        </Alert>
      )}

      {/* Rate Limiting Settings */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Speed color="primary" />
            <Typography variant="h6">Rate Limiting</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.rateLimiting.enabled}
                    onChange={(e) => handleSettingChange('rateLimiting', 'enabled', e.target.checked)}
                  />
                }
                label="Enable Rate Limiting"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Requests per Minute"
                type="number"
                value={settings.rateLimiting.requestsPerMinute}
                onChange={(e) => handleSettingChange('rateLimiting', 'requestsPerMinute', parseInt(e.target.value))}
                disabled={!settings.rateLimiting.enabled}
                helperText="Maximum requests allowed per minute per client"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Burst Limit"
                type="number"
                value={settings.rateLimiting.burstLimit}
                onChange={(e) => handleSettingChange('rateLimiting', 'burstLimit', parseInt(e.target.value))}
                disabled={!settings.rateLimiting.enabled}
                helperText="Maximum burst requests allowed"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Security Settings */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Security color="primary" />
            <Typography variant="h6">Security</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.security.requireAttestation}
                    onChange={(e) => handleSettingChange('security', 'requireAttestation', e.target.checked)}
                  />
                }
                label="Require Device Attestation"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.security.allowSoftwareAttestation}
                    onChange={(e) => handleSettingChange('security', 'allowSoftwareAttestation', e.target.checked)}
                    disabled={!settings.security.requireAttestation}
                  />
                }
                label="Allow Software Attestation"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.security.blockFailedDevices}
                    onChange={(e) => handleSettingChange('security', 'blockFailedDevices', e.target.checked)}
                  />
                }
                label="Block Devices with Failed Attestations"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Max Failed Attempts"
                type="number"
                value={settings.security.maxFailedAttempts}
                onChange={(e) => handleSettingChange('security', 'maxFailedAttempts', parseInt(e.target.value))}
                disabled={!settings.security.blockFailedDevices}
                helperText="Number of failed attempts before blocking"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Logging Settings */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Storage color="primary" />
            <Typography variant="h6">Logging</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Log Level</InputLabel>
                <Select
                  value={settings.logging.level}
                  label="Log Level"
                  onChange={(e) => handleSettingChange('logging', 'level', e.target.value)}
                >
                  <MenuItem value="DEBUG">DEBUG</MenuItem>
                  <MenuItem value="INFO">INFO</MenuItem>
                  <MenuItem value="WARNING">WARNING</MenuItem>
                  <MenuItem value="ERROR">ERROR</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Retention Days"
                type="number"
                value={settings.logging.retentionDays}
                onChange={(e) => handleSettingChange('logging', 'retentionDays', parseInt(e.target.value))}
                helperText="Number of days to keep logs"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.logging.enableAuditLog}
                    onChange={(e) => handleSettingChange('logging', 'enableAuditLog', e.target.checked)}
                  />
                }
                label="Enable Audit Logging"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Notification Settings */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Notifications color="primary" />
            <Typography variant="h6">Notifications</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Alert Email Address"
                type="email"
                value={settings.notifications.alertEmail}
                onChange={(e) => handleSettingChange('notifications', 'alertEmail', e.target.value)}
                helperText="Email address for system alerts"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.emailAlerts}
                    onChange={(e) => handleSettingChange('notifications', 'emailAlerts', e.target.checked)}
                    disabled={!settings.notifications.alertEmail}
                  />
                }
                label="Enable Email Alerts"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.securityAlerts}
                    onChange={(e) => handleSettingChange('notifications', 'securityAlerts', e.target.checked)}
                  />
                }
                label="Security Alerts"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.notifications.systemAlerts}
                    onChange={(e) => handleSettingChange('notifications', 'systemAlerts', e.target.checked)}
                  />
                }
                label="System Alerts"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Performance Settings */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Speed color="primary" />
            <Typography variant="h6">Performance</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.performance.cacheEnabled}
                    onChange={(e) => handleSettingChange('performance', 'cacheEnabled', e.target.checked)}
                  />
                }
                label="Enable Caching"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cache TTL (Minutes)"
                type="number"
                value={settings.performance.cacheTtlMinutes}
                onChange={(e) => handleSettingChange('performance', 'cacheTtlMinutes', parseInt(e.target.value))}
                disabled={!settings.performance.cacheEnabled}
                helperText="Cache time-to-live in minutes"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Connection Pool Size"
                type="number"
                value={settings.performance.connectionPoolSize}
                onChange={(e) => handleSettingChange('performance', 'connectionPoolSize', parseInt(e.target.value))}
                helperText="Database connection pool size"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* System Information */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Information
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="Server Version"
                secondary="Skywalk Licensing Server v1.0.0"
              />
              <ListItemSecondaryAction>
                <Chip label="Latest" color="success" size="small" />
              </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primary="Database Status"
                secondary="Azure SQL Database - Connected"
              />
              <ListItemSecondaryAction>
                <Chip label="Healthy" color="success" size="small" />
              </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primary="Redis Status"
                secondary="Redis Cache - Connected"
              />
              <ListItemSecondaryAction>
                <Chip label="Healthy" color="success" size="small" />
              </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primary="ML Models"
                secondary="Phase 6 Advanced Analytics"
              />
              <ListItemSecondaryAction>
                <Chip label="Active" color="primary" size="small" />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Reset Confirmation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Reset Settings</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to reset all settings to their default values? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={confirmReset} color="warning" variant="contained">
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settings; 